/**
 * Routes Configuration
 * 
 * Centralized routing configuration following best practices:
 * - Separation of concerns
 * - Easy to maintain and scale
 * - Clear route structure
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Components
import AuthGuard from '../components/AuthGuard';
import LandingPage from '../components/LandingPage';
import CVOLogin from '../components/CVOLogin';
import AuthorityLogin from '../components/AuthorityLogin';
import CreateVigilance from '../components/CreateVigilance';
import VigilanceValidation from '../components/VigilanceValidation';
import VigilanceStatus from '../components/VigilanceStatus';
import CVODashboard from '../components/CVODashboard';
import AuthorityDashboard from '../components/AuthorityDashboard';
import BarredUserMessage from '../components/BarredUserMessage';

// CVO Route Components
import {
  CVOInboxView,
  CVOComplaintsView,
  CVOOfficersView,
  CVOSettingsView,
  CVOAddCVOView
} from '../views/cvo';

// User Route Components
import {
  VigilanceLoginView,
  VigilanceSignupView,
  VigilanceDashboardView,
  AllComplaintsView,
  CreateVigilanceView,
  ComplaintHistoryView
} from '../views/user';

// Authority Route Components
import {
  AuthorityInboxView,
  AuthorityAssignedComplaintsView,
  AuthorityAddOfflineComplaintView,
  AuthorityOfflineComplaintsView,
  AuthorityOfficersView
} from '../views/authority';

/**
 * Public Routes - Accessible to everyone
 */
const PublicRoutes = () => (
  <>
    <Route 
      path="/" 
      element={
        <AuthGuard>
          <LandingPage />
        </AuthGuard>
      } 
    />
    <Route 
      path="/vigilance-portal" 
      element={
        <AuthGuard>
          <CreateVigilance />
        </AuthGuard>
      } 
    />
    <Route 
      path="/vigilance-validation" 
      element={
        <AuthGuard>
          <VigilanceValidation />
        </AuthGuard>
      } 
    />
    <Route 
      path="/vigilance-status" 
      element={
        <AuthGuard>
          <VigilanceStatus />
        </AuthGuard>
      } 
    />
    <Route path="/barred-user" element={<BarredUserMessage />} />
  </>
);

/**
 * Auth Routes - Login pages redirect authenticated users to dashboard
 */
const AuthRoutes = () => (
  <>
    <Route 
      path="/cvo-login" 
      element={
        <AuthGuard requireGuest>
          <CVOLogin />
        </AuthGuard>
      } 
    />
    <Route 
      path="/authority-login" 
      element={
        <AuthGuard requireGuest>
          <AuthorityLogin />
        </AuthGuard>
      } 
    />
    <Route 
      path="/vigilance-login" 
      element={
        <AuthGuard requireGuest>
          <VigilanceLoginView />
        </AuthGuard>
      } 
    />
    <Route 
      path="/vigilance-signup" 
      element={
        <AuthGuard requireGuest>
          <VigilanceSignupView />
        </AuthGuard>
      } 
    />
  </>
);

/**
 * CVO Dashboard Routes - Nested routes for CVO dashboard tabs
 */
const CVODashboardRoutes = () => (
  <>
    <Route index element={<CVOInboxView />} />
    <Route path="inbox" element={<CVOInboxView />} />
    <Route path="complaints" element={<CVOComplaintsView />} />
    <Route path="officers" element={<CVOOfficersView />} />
    <Route path="settings" element={<CVOSettingsView />} />
    <Route path="cvos" element={<CVOAddCVOView />} />
  </>
);

/**
 * Authority Dashboard Routes - Nested routes for Authority dashboard tabs
 */
const AuthorityDashboardRoutes = () => (
  <>
    <Route index element={<AuthorityInboxView />} />
    <Route path="inbox" element={<AuthorityInboxView />} />
    <Route path="assigned-complaints" element={<AuthorityAssignedComplaintsView />} />
    <Route path="offline-complaints" element={<AuthorityOfflineComplaintsView />} />
    <Route path="add-offline" element={<AuthorityAddOfflineComplaintView />} />
    <Route path="offline-complain" element={<AuthorityAddOfflineComplaintView />} />
    <Route path="officers" element={<AuthorityOfficersView />} />
  </>
);

/**
 * User Dashboard Routes - Nested routes for User dashboard tabs
 */
const UserDashboardRoutes = () => (
  <>
    <Route index element={<VigilanceDashboardView />} />
    <Route path="complaints" element={<AllComplaintsView />} />
    <Route path="create" element={<CreateVigilanceView />} />
    <Route path="complaint-history" element={<ComplaintHistoryView />} />
  </>
);

/**
 * Protected Routes - Require authentication
 */
const ProtectedRoutes = () => (
  <>
    <Route 
      path="/cvo-dashboard/*" 
      element={
        <AuthGuard requireAuth requiredRole="cvo">
          <CVODashboard />
        </AuthGuard>
      } 
    >
      {CVODashboardRoutes()}
    </Route>
    <Route 
      path="/authority-dashboard/*" 
      element={
        <AuthGuard requireAuth requiredRole="authority">
          <AuthorityDashboard />
        </AuthGuard>
      } 
    >
      {AuthorityDashboardRoutes()}
    </Route>
    <Route 
      path="/vigilance-dashboard/*" 
      element={
        <AuthGuard requireAuth>
          <VigilanceDashboardView />
        </AuthGuard>
      } 
    >
      {UserDashboardRoutes()}
    </Route>
    <Route 
      path="/user/complaint-history" 
      element={
        <AuthGuard requireAuth>
          <ComplaintHistoryView />
        </AuthGuard>
      } 
    />
  </>
);

/**
 * Main App Routes Component
 */
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/index.html" element={<Navigate to="/" replace />} />
      {PublicRoutes()}
      {AuthRoutes()}
      {ProtectedRoutes()}
    </Routes>
  );
};

export default AppRoutes;

